/**
 * AI Mixing Assistant Panel
 * LLM-powered mixing suggestions UI
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import {
  Brain,
  Sparkles,
  RefreshCw,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Volume2,
  Sliders,
  Music,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  X,
  Gauge,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Priority badge colors
const PRIORITY_COLORS = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

// Category icons
const CATEGORY_ICONS = {
  mixing: Volume2,
  mastering: Gauge,
  arrangement: Music,
};

// Single suggestion card
const SuggestionCard = ({ suggestion, onApply, onDismiss }) => {
  const [applied, setApplied] = useState(false);
  const IconComponent = CATEGORY_ICONS[suggestion.category] || Sliders;
  
  const handleApply = () => {
    if (onApply) {
      onApply(suggestion);
      setApplied(true);
      toast.success('Suggestion applied!');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`p-3 rounded-lg border ${
        applied ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-800/50 border-zinc-700/50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
          <IconComponent className="w-4 h-4 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Track name and type */}
          <div className="flex items-center gap-2 mb-1">
            {suggestion.trackName && (
              <span className="text-xs font-medium text-indigo-400">{suggestion.trackName}</span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[suggestion.priority]}`}>
              {suggestion.priority}
            </span>
          </div>
          
          {/* Suggestion text */}
          <p className="text-sm text-zinc-300 mb-2">{suggestion.reason}</p>
          
          {/* Value change indicator */}
          {suggestion.currentValue !== null && suggestion.suggestedValue !== null && (
            <div className="flex items-center gap-2 text-xs mb-2">
              <span className="text-zinc-500">{suggestion.parameter}:</span>
              <span className="text-zinc-400 font-mono">{suggestion.currentValue.toFixed(2)}</span>
              <ArrowRight className="w-3 h-3 text-zinc-500" />
              <span className="text-emerald-400 font-mono">{suggestion.suggestedValue.toFixed(2)}</span>
            </div>
          )}
          
          {/* Actions */}
          {!applied && onApply && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleApply}
                className="h-7 px-2 text-emerald-400 hover:bg-emerald-500/20"
              >
                <Check className="w-3 h-3 mr-1" />
                Apply
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDismiss(suggestion)}
                className="h-7 px-2 text-zinc-400 hover:text-zinc-300"
              >
                <X className="w-3 h-3 mr-1" />
                Dismiss
              </Button>
            </div>
          )}
          
          {applied && (
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Applied
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Mix score gauge
const MixScoreGauge = ({ score }) => {
  const getColor = () => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };
  
  const getLabel = () => {
    if (score >= 80) return 'Great';
    if (score >= 60) return 'Good';
    return 'Needs Work';
  };
  
  return (
    <div className="text-center py-4">
      <div className={`text-5xl font-bold ${getColor()}`}>{score}</div>
      <div className="text-sm text-zinc-400 mt-1">Mix Score</div>
      <div className={`text-xs ${getColor()} mt-0.5`}>{getLabel()}</div>
    </div>
  );
};

// Main AI Mixing Panel
export const AIMixingPanel = ({
  projectId,
  projectName,
  bpm,
  tracks,
  masterVolume,
  genre,
  onApplySuggestion,
  onClose,
  isVisible = true,
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showTips, setShowTips] = useState(true);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());

  const handleAnalyze = useCallback(async () => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setError(null);
    setResult(null);
    setDismissedSuggestions(new Set());
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setAnalysisProgress((prev) => Math.min(prev + 5, 90));
    }, 150);
    
    try {
      const trackAnalysis = tracks.map(track => ({
        id: String(track.id),
        name: track.name || `Track ${track.id}`,
        type: track.type || 'audio',  // Default to 'audio' if not specified
        volume: track.volume || 0.8,
        pan: track.pan || 0,
        muted: track.muted || false,
        solo: track.solo || false,
        hasAudio: track.hasAudio || !!track.audioBuffer,
        eq: track.eq,
        effects: track.effects,
      }));
      
      const response = await fetch(`${API_URL}/api/ai-mixing/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectId || `temp-${Date.now()}`,
          projectName: projectName || 'Untitled Project',
          bpm: bpm || 120,
          genre: genre || null,
          tracks: trackAnalysis,
          masterVolume: masterVolume || 0.8,
          analysisType: 'general',
        }),
      });
      
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      
      if (!response.ok) {
        throw new Error('Analysis failed');
      }
      
      const data = await response.json();
      
      // Short delay to show 100%
      await new Promise(r => setTimeout(r, 300));
      setResult(data);
      
    } catch (err) {
      clearInterval(progressInterval);
      setError('Failed to analyze mix. Please try again.');
      console.error('AI Mixing error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [projectId, projectName, bpm, genre, tracks, masterVolume]);

  const handleDismiss = (suggestion) => {
    setDismissedSuggestions(prev => new Set([...prev, suggestion.trackId + suggestion.parameter]));
  };

  const visibleSuggestions = result?.suggestions?.filter(
    s => !dismissedSuggestions.has(s.trackId + s.parameter)
  ) || [];

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-96 bg-zinc-900/95 backdrop-blur-md border-l border-zinc-700 flex flex-col h-full"
      data-testid="ai-mixing-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">AI Mixing Assistant</h3>
            <p className="text-xs text-zinc-400">Intelligent suggestions for your mix</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Analysis Progress */}
        {isAnalyzing && (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />
              <span className="text-sm text-zinc-300">Analyzing your mix...</span>
            </div>
            <Progress value={analysisProgress} className="h-1.5" />
            <p className="text-xs text-zinc-500 mt-2">
              AI is evaluating volume levels, panning, EQ balance, and more...
            </p>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="p-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Results */}
        {result && !isAnalyzing && (
          <div className="p-4 space-y-4">
            {/* Mix Score */}
            <div className="bg-zinc-800/50 rounded-lg border border-zinc-700/50">
              <MixScoreGauge score={result.mixScore} />
            </div>
            
            {/* Overall Assessment */}
            <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-medium text-white">Assessment</span>
              </div>
              <p className="text-sm text-zinc-300">{result.overallAssessment}</p>
            </div>
            
            {/* Suggestions */}
            {visibleSuggestions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-white">
                    Suggestions ({visibleSuggestions.length})
                  </h4>
                  <button
                    onClick={() => visibleSuggestions.forEach(s => onApplySuggestion(s))}
                    className="text-xs px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full hover:bg-green-500/30 transition-colors"
                    data-testid="apply-all-suggestions-btn"
                  >
                    Apply All
                  </button>
                </div>
                <div className="space-y-2">
                  {visibleSuggestions.map((suggestion, index) => (
                    <SuggestionCard
                      key={`${suggestion.trackId}-${suggestion.parameter}-${index}`}
                      suggestion={suggestion}
                      onApply={onApplySuggestion}
                      onDismiss={handleDismiss}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Tips */}
            {result.tips?.length > 0 && (
              <div className="border-t border-zinc-800 pt-4">
                <button
                  onClick={() => setShowTips(!showTips)}
                  className="w-full flex items-center justify-between text-sm text-zinc-300 mb-2"
                >
                  <span className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-400" />
                    Pro Tips
                  </span>
                  {showTips ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                <AnimatePresence>
                  {showTips && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-2 overflow-hidden"
                    >
                      {result.tips.map((tip, index) => (
                        <li key={index} className="flex items-start gap-2 text-xs text-zinc-400">
                          <span className="text-amber-400 mt-0.5">•</span>
                          {tip}
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
        
        {/* Initial State */}
        {!result && !isAnalyzing && !error && (
          <div className="p-4 text-center py-12">
            <Brain className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
            <h4 className="text-lg font-medium text-white mb-2">Ready to Analyze</h4>
            <p className="text-sm text-zinc-400 mb-4">
              Get AI-powered suggestions to improve your mix
            </p>
            <p className="text-xs text-zinc-500 mb-6">
              {tracks.length} tracks • {bpm} BPM • {genre || 'Genre not set'}
            </p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-4 border-t border-zinc-800">
        <Button
          onClick={handleAnalyze}
          disabled={isAnalyzing || tracks.length === 0}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
          data-testid="analyze-mix-btn"
        >
          {isAnalyzing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : result ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Re-analyze Mix
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze My Mix
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default AIMixingPanel;
